import React from 'react';

const ChordDiagram = ({ chord, size = 'medium' }) => {
  const { positions, fingers, name } = chord;
  
  const sizes = {
    small: { width: 100, height: 140, stringGap: 14, fretGap: 20, namePadding: 18 },
    medium: { width: 140, height: 185, stringGap: 20, fretGap: 28, namePadding: 22 },
    large: { width: 180, height: 230, stringGap: 26, fretGap: 36, namePadding: 28 }
  };

  const { width, height, stringGap, fretGap, namePadding } = sizes[size];
  const startX = 20;
  const startY = 30;
  const numStrings = 6;
  const numFrets = 4;

  // Find the minimum fret (excluding 0 and -1) to determine if we need to show a fret number
  const playedFrets = positions.filter(p => p > 0);
  const minFret = playedFrets.length > 0 ? Math.min(...playedFrets) : 1;
  const baseFret = minFret > 3 ? minFret : 1;

  return (
    <svg 
      width={width} 
      height={height} 
      className="chord-diagram"
      viewBox={`0 0 ${width} ${height}`}
    >
      {/* Nut (thick line at top if baseFret is 1) */}
      {baseFret === 1 && (
        <rect
          x={startX - 2}
          y={startY - 4}
          width={stringGap * (numStrings - 1) + 4}
          height={4}
          fill="#FF9F1C"
          rx={1}
        />
      )}

      {/* Fret number indicator */}
      {baseFret > 1 && (
        <text
          x={startX - 14}
          y={startY + fretGap / 2 + 4}
          fill="#A1A1AA"
          fontSize="12"
          fontFamily="JetBrains Mono"
        >
          {baseFret}
        </text>
      )}

      {/* Frets (horizontal lines) */}
      {[...Array(numFrets + 1)].map((_, i) => (
        <line
          key={`fret-${i}`}
          x1={startX}
          y1={startY + i * fretGap}
          x2={startX + stringGap * (numStrings - 1)}
          y2={startY + i * fretGap}
          stroke="#3F3F46"
          strokeWidth={i === 0 && baseFret === 1 ? 0 : 1}
        />
      ))}

      {/* Strings (vertical lines) */}
      {[...Array(numStrings)].map((_, i) => (
        <line
          key={`string-${i}`}
          x1={startX + i * stringGap}
          y1={startY}
          x2={startX + i * stringGap}
          y2={startY + fretGap * numFrets}
          stroke="#71717A"
          strokeWidth={1 + (5 - i) * 0.2}
        />
      ))}

      {/* Finger positions */}
      {positions.map((pos, stringIndex) => {
        const x = startX + stringIndex * stringGap;
        
        if (pos === -1) {
          // Muted string (X)
          return (
            <text
              key={`pos-${stringIndex}`}
              x={x}
              y={startY - 10}
              textAnchor="middle"
              fill="#EF4444"
              fontSize="14"
              fontWeight="bold"
            >
              ×
            </text>
          );
        }
        
        if (pos === 0) {
          // Open string (O)
          return (
            <circle
              key={`pos-${stringIndex}`}
              cx={x}
              cy={startY - 10}
              r={5}
              fill="none"
              stroke="#10B981"
              strokeWidth={2}
            />
          );
        }

        // Fretted note
        const adjustedFret = pos - baseFret + 1;
        if (adjustedFret >= 1 && adjustedFret <= numFrets) {
          const y = startY + (adjustedFret - 0.5) * fretGap;
          const finger = fingers[stringIndex];
          
          return (
            <g key={`pos-${stringIndex}`}>
              <circle
                cx={x}
                cy={y}
                r={size === 'small' ? 6 : size === 'medium' ? 8 : 10}
                fill="#FF9F1C"
                className="drop-shadow-lg"
              />
              {finger > 0 && (
                <text
                  x={x}
                  y={y + 4}
                  textAnchor="middle"
                  fill="#000"
                  fontSize={size === 'small' ? '8' : '10'}
                  fontWeight="bold"
                  fontFamily="JetBrains Mono"
                >
                  {finger}
                </text>
              )}
            </g>
          );
        }
        
        return null;
      })}

      {/* Chord name - positioned below the fretboard */}
      <text
        x={width / 2}
        y={startY + fretGap * numFrets + namePadding}
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize={size === 'small' ? '12' : size === 'medium' ? '14' : '16'}
        fontWeight="bold"
        fontFamily="Outfit"
      >
        {name}
      </text>
    </svg>
  );
};

export default ChordDiagram;
